import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getGetAssessmentsAccumulationMappingsQueryKey,
  usePatchAssessmentsAccumulationMapping,
} from '@/entities/assessments/api/generated/api';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { Button, Form, Text, toast } from '@/shared/ui';
import { Textarea } from '@/shared/ui/form/textarea/Textarea';

type MemoFormValues = {
  memo: string;
};

type Props = {
  assessmentsAccumulationId: string;
  initialMemo: string;
  isMemoEditing: boolean;
  onMemoEditEnd: () => void;
};

/**
 * メモ表示・編集コンポーネント。
 * 編集の開始/終了は親アコーディオンが管理する。
 */
export const AssessmentComparisonManagement = ({
  assessmentsAccumulationId,
  initialMemo,
  isMemoEditing,
  onMemoEditEnd,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm<MemoFormValues>({
    defaultValues: { memo: initialMemo },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields, isSubmitting, isSubmitSuccessful },
  } = form;

  useEffect(() => {
    reset({ memo: initialMemo });
  }, [initialMemo, reset]);

  const setSidePanelHasUnsavedChanges = useRouteSidePanelStore((s) => s.setHasUnsavedChanges);

  useEffect(() => {
    setSidePanelHasUnsavedChanges(isMemoEditing && isDirty);
  }, [isMemoEditing, isDirty, setSidePanelHasUnsavedChanges]);

  useEffect(() => {
    return () => {
      setSidePanelHasUnsavedChanges(false);
    };
  }, [setSidePanelHasUnsavedChanges]);

  const { mutate: patchMemo, isPending: isSaving } = usePatchAssessmentsAccumulationMapping();

  const handleCancel = () => {
    reset({ memo: initialMemo });
    setSidePanelHasUnsavedChanges(false);
    onMemoEditEnd();
  };

  const onSubmit = (data: MemoFormValues) => {
    patchMemo(
      {
        assessmentsAccumulationId,
        data: { memo: data.memo },
      },
      {
        onSuccess: () => {
          toast.success(t('common.messages.update-success', { name: t('assessment.memo.title') }));
          reset({ memo: data.memo });
          setSidePanelHasUnsavedChanges(false);
          queryClient.invalidateQueries({
            queryKey: getGetAssessmentsAccumulationMappingsQueryKey(assessmentsAccumulationId),
          });
          onMemoEditEnd();
        },
        onError: () => {
          toast.error(t('common.messages.update-failed', { name: t('assessment.memo.title') }));
        },
      },
    );
  };

  if (!isMemoEditing) {
    return <Text className="whitespace-pre-wrap">{initialMemo}</Text>;
  }

  return (
    <Form.Provider {...form}>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 py-2"
        leavePageAlertCondition={{
          dirtyFields,
          isSubmitting,
          isSubmitSuccessful,
        }}
      >
        <Controller
          control={control}
          name="memo"
          render={({ field }) => (
            <Textarea
              {...field}
              maxLength={2000}
              rows={4}
              placeholder={t('assessment.memo.placeholder')}
              className="min-w-full"
            />
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isSaving}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isSaving || !isDirty}>
            {t('common.actions.save')}
          </Button>
        </div>
      </Form>
    </Form.Provider>
  );
};
